// src/contexts/AuthContext.js
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { getToken, getUser, logout as doLogout } from "../services/auth";
import { SESSION_EXPIRED_EVENT } from "../services/session";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const toast = useToast();
  const [token, setToken] = useState(() => getToken());
  const [user, setUser] = useState(() => getUser());

  const notifySessionExpired = useCallback(
    (message) => {
      const toastId = "session-expired";
      if (toast.isActive?.(toastId)) return;
      toast({
        id: toastId,
        status: "warning",
        title: "세션이 만료되었습니다.",
        description: message,
        duration: 5000,
        isClosable: true,
      });
    },
    [toast]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleSessionExpired = (event) => {
      const message = String((event?.detail?.message) || "세션이 만료되었습니다.");
      doLogout();
      setToken(null);
      setUser({});
      notifySessionExpired(message);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [notifySessionExpired, setToken, setUser]);

  const value = useMemo(() => ({
    token,
    user,
    setToken,
    setUser,
    logout: () => { doLogout(); setToken(null); setUser({}); },
  }), [token, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
